// AGC: Translation Manager Admin Component
// AGC: Full-featured UI for managing multi-language translations

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Search, Download, Plus, AlertTriangle, Check, X, Globe, RefreshCw, Database } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../services/firebase-auth'
import { getBackendUrl } from '../../utils/backendUrl'

// AGC: API base URL for write operations (saving translations)
const API_BASE = getBackendUrl()

// AGC: Available namespaces
const NAMESPACES = [
  'common',
  'admin', 
  'auth',
  'fields',
  'tasks',
  'weather',
  'financial',
  'profile',
  'notifications',
  'voice',
] as const

// AGC: Available languages
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
]

type Namespace = typeof NAMESPACES[number]
type LanguageCode = 'en' | 'pt' | 'es'

interface TranslationEntry {
  key: string
  namespace: Namespace
  values: Record<LanguageCode, string>
  hasChanges: boolean
  isMissing: Record<LanguageCode, boolean>
}

/**
 * AGC: TranslationManager - Admin panel for managing translations
 */
export const TranslationManager: React.FC = () => {
  // AGC: State
  const [translations, setTranslations] = useState<Record<LanguageCode, Record<string, object>>>({
    en: {},
    pt: {},
    es: {},
  })
  const [entries, setEntries] = useState<TranslationEntry[]>([])
  const [selectedNamespace, setSelectedNamespace] = useState<Namespace | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [editingCell, setEditingCell] = useState<{ key: string; lang: LanguageCode } | null>(null)
  const [editValue, setEditValue] = useState('')
  const [showAddKeyModal, setShowAddKeyModal] = useState(false)
  const [newKey, setNewKey] = useState({ namespace: 'common' as Namespace, key: '', en: '', pt: '', es: '' })
  const [showMissingFor, setShowMissingFor] = useState<LanguageCode | null>(null)

  /**
   * AGC: Fetch all translations directly from Firestore
   * AGC: Falls back to backend API if Firestore is unavailable
   */
  const fetchAllTranslations = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const results: Record<LanguageCode, Record<string, object>> = { en: {}, pt: {}, es: {} }
      
      // AGC: Try to fetch from Firestore first
      if (db) {
        for (const lang of LANGUAGES) {
          try {
            const docRef = doc(db, 'translations', lang.code)
            const docSnap = await getDoc(docRef)
            
            if (docSnap.exists()) {
              const data = docSnap.data()
              results[lang.code as LanguageCode] = data || {}
              console.log(`✅ AGC: Loaded ${lang.code} translations from Firestore`)
            } else {
              console.log(`⚠️ AGC: No translations in Firestore for ${lang.code}`)
            }
          } catch (err) {
            console.warn(`⚠️ AGC: Error fetching ${lang.code} from Firestore:`, err)
          }
        }
      } else {
        console.log('⚠️ AGC: Firestore not available, trying backend API...')
      }
      
      // AGC: If Firestore didn't return any data, try backend API as fallback
      const hasData = Object.values(results).some(langData => Object.keys(langData).length > 0)
      if (!hasData && API_BASE) {
        console.log('🔄 AGC: Trying backend API as fallback...')
        for (const lang of LANGUAGES) {
          try {
            const response = await fetch(`${API_BASE}/api/v1/translations/${lang.code}`)
            if (response.ok) {
              const data = await response.json()
              if (data.success && data.data) {
                results[lang.code as LanguageCode] = data.data || {}
                console.log(`✅ AGC: Loaded ${lang.code} translations from backend API`)
              }
            }
          } catch (err) {
            console.warn(`⚠️ AGC: Backend API not available for ${lang.code}`)
          }
        }
      }

      setTranslations(results)
      
      // AGC: Build entries list
      const allEntries = buildEntriesList(results)
      setEntries(allEntries)
    } catch (err) {
      setError('Failed to fetch translations')
      console.error('❌ AGC: Error fetching translations:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * AGC: Flatten nested object to dot notation
   * AGC: Example: { categories: { fuel: 'Fuel' } } => { 'categories.fuel': 'Fuel' }
   */
  const flattenObject = (obj: Record<string, unknown>, prefix = ''): Record<string, string> => {
    const result: Record<string, string> = {}
    
    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}.${key}` : key
      
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // AGC: Recursively flatten nested objects
        Object.assign(result, flattenObject(value as Record<string, unknown>, newKey))
      } else if (typeof value === 'string') {
        result[newKey] = value
      }
    }
    
    return result
  }

  /**
   * AGC: Build flat entries list from nested translations
   */
  const buildEntriesList = (data: Record<LanguageCode, Record<string, object>>): TranslationEntry[] => {
    const entriesMap = new Map<string, TranslationEntry>()

    // AGC: Process each language
    for (const langCode of ['en', 'pt', 'es'] as LanguageCode[]) {
      const langData = data[langCode] || {}
      
      // AGC: Process each namespace
      for (const ns of NAMESPACES) {
        const nsData = (langData[ns] || {}) as Record<string, unknown>
        
        // AGC: Flatten nested objects (like categories)
        const flatData = flattenObject(nsData)
        
        // AGC: Process each key
        for (const [key, value] of Object.entries(flatData)) {
          const fullKey = `${ns}.${key}`
          
          if (!entriesMap.has(fullKey)) {
            entriesMap.set(fullKey, {
              key,
              namespace: ns,
              values: { en: '', pt: '', es: '' },
              hasChanges: false,
              isMissing: { en: true, pt: true, es: true },
            })
          }

          const entry = entriesMap.get(fullKey)!
          entry.values[langCode] = value
          entry.isMissing[langCode] = !value || value.trim() === ''
        }
      }
    }

    return Array.from(entriesMap.values()).sort((a, b) => {
      if (a.namespace !== b.namespace) {
        return a.namespace.localeCompare(b.namespace)
      }
      return a.key.localeCompare(b.key)
    })
  }

  // AGC: Fetch on mount
  useEffect(() => {
    fetchAllTranslations()
  }, [fetchAllTranslations])

  /**
   * AGC: Filter entries by search, namespace, and missing status
   */
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // AGC: Filter by namespace
      if (selectedNamespace !== 'all' && entry.namespace !== selectedNamespace) {
        return false
      }

      // AGC: Filter by missing status (if showMissingFor is set)
      if (showMissingFor !== null && !entry.isMissing[showMissingFor]) {
        return false
      }

      // AGC: Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesKey = entry.key.toLowerCase().includes(query)
        const matchesValue = Object.values(entry.values).some(v => 
          v.toLowerCase().includes(query)
        )
        return matchesKey || matchesValue
      }

      return true
    })
  }, [entries, selectedNamespace, searchQuery, showMissingFor])

  /**
   * AGC: Start editing a cell
   */
  const startEdit = (key: string, namespace: Namespace, lang: LanguageCode, currentValue: string) => {
    setEditingCell({ key: `${namespace}.${key}`, lang })
    setEditValue(currentValue)
  }

  /**
   * AGC: Save cell edit
   * AGC: Handles nested keys like 'categories.fuel' properly
   */
  const saveEdit = async () => {
    if (!editingCell) return

    // AGC: Split only on first dot to get namespace, rest is key
    const firstDotIndex = editingCell.key.indexOf('.')
    const namespace = editingCell.key.substring(0, firstDotIndex) as Namespace
    const key = editingCell.key.substring(firstDotIndex + 1)
    
    // AGC: For API call, encode the key to handle dots in nested keys
    const encodedKey = encodeURIComponent(key)
    
    setIsSaving(true)
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/translations/${editingCell.lang}/${namespace}/${encodedKey}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: editValue }),
        }
      )

      if (response.ok) {
        // AGC: Update local state
        setEntries(prev => prev.map(entry => {
          if (entry.namespace === namespace && entry.key === key) {
            return {
              ...entry,
              values: { ...entry.values, [editingCell.lang]: editValue },
              isMissing: { ...entry.isMissing, [editingCell.lang]: !editValue.trim() },
            }
          }
          return entry
        }))
        
        setSuccessMessage('Translation saved!')
        setTimeout(() => setSuccessMessage(null), 2000)
      } else {
        throw new Error('Failed to save')
      }
    } catch (err) {
      setError('Failed to save translation')
    } finally {
      setIsSaving(false)
      setEditingCell(null)
      setEditValue('')
    }
  }

  /**
   * AGC: Cancel edit
   */
  const cancelEdit = () => {
    setEditingCell(null)
    setEditValue('')
  }

  /**
   * AGC: Add new translation key
   */
  /**
   * AGC: Add new translation key
   * AGC: Handles nested keys properly with URL encoding
   */
  const addNewKey = async () => {
    if (!newKey.key || !newKey.en) {
      setError('Key and English value are required')
      return
    }

    // AGC: Encode the key to handle dots in nested keys
    const encodedKey = encodeURIComponent(newKey.key)

    setIsSaving(true)
    try {
      // AGC: Save for each language
      for (const lang of ['en', 'pt', 'es'] as LanguageCode[]) {
        const value = newKey[lang]
        if (value) {
          await fetch(
            `${API_BASE}/api/v1/translations/${lang}/${newKey.namespace}/${encodedKey}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ value }),
            }
          )
        }
      }

      // AGC: Add to local state
      setEntries(prev => [
        ...prev,
        {
          key: newKey.key,
          namespace: newKey.namespace,
          values: { en: newKey.en, pt: newKey.pt, es: newKey.es },
          hasChanges: false,
          isMissing: { 
            en: !newKey.en.trim(), 
            pt: !newKey.pt.trim(), 
            es: !newKey.es.trim() 
          },
        },
      ])

      setShowAddKeyModal(false)
      setNewKey({ namespace: 'common', key: '', en: '', pt: '', es: '' })
      setSuccessMessage('New translation key added!')
      setTimeout(() => setSuccessMessage(null), 2000)
    } catch (err) {
      setError('Failed to add translation key')
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * AGC: Export translations as JSON
   */
  const exportTranslations = (lang: LanguageCode) => {
    const data = translations[lang]
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `translations_${lang}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  /**
   * AGC: Import translations from JSON file
   */
  const importTranslations = async (lang: LanguageCode, file: File) => {
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      
      const response = await fetch(`${API_BASE}/api/v1/translations/${lang}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ translations: data }),
      })

      if (response.ok) {
        setSuccessMessage(`Imported translations for ${lang}!`)
        fetchAllTranslations()
      } else {
        throw new Error('Import failed')
      }
    } catch (err) {
      setError('Failed to import translations. Check file format.')
    }
  }

  /**
   * AGC: Seed all translations from local files to Firestore
   */
  const seedToFirestore = async () => {
    setIsSaving(true)
    setError(null)
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/translations/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        const data = await response.json()
        const results = data.data || []
        const totalKeys = results.reduce((sum: number, r: any) => sum + (r.keyCount || 0), 0)
        const totalNew = results.reduce((sum: number, r: any) => sum + (r.newKeys || 0), 0)
        const totalPreserved = results.reduce((sum: number, r: any) => sum + (r.preservedKeys || 0), 0)
        
        setSuccessMessage(
          `✅ Seeded ${totalKeys} keys (${totalNew} new, ${totalPreserved} preserved from Firestore)!`
        )
        setTimeout(() => setSuccessMessage(null), 7000)
        
        // AGC: Refresh to load from Firestore
        setTimeout(() => fetchAllTranslations(), 1000)
      } else {
        throw new Error('Seed failed')
      }
    } catch (err) {
      setError('Failed to seed translations to Firestore')
      console.error('❌ AGC: Error seeding:', err)
    } finally {
      setIsSaving(false)
    }
  }

  // AGC: Count missing translations
  const missingCount = useMemo(() => {
    const counts: Record<LanguageCode, number> = { en: 0, pt: 0, es: 0 }
    entries.forEach(entry => {
      for (const lang of ['en', 'pt', 'es'] as LanguageCode[]) {
        if (entry.isMissing[lang]) counts[lang]++
      }
    })
    return counts
  }, [entries])

  return (
    <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none">
      {/* AGC: Header */}
      <div className="p-6 border-b border-slate-200 dark:border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-info-700 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">Translation Manager</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchAllTranslations()}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={seedToFirestore}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-info-700 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:opacity-90 transition-colors disabled:opacity-50"
              title="Upload all translations from local files to Firestore"
            >
              <Database className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              {isSaving ? 'Seeding...' : 'Seed to Firestore'}
            </button>
            
            <button
              onClick={() => setShowAddKeyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand-primary dark:bg-brand-light text-white rounded-lg hover:bg-brand-dark dark:hover:opacity-90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Key
            </button>
          </div>
        </div>

        {/* AGC: Stats */}
        <div className="flex items-center gap-6 mb-4 flex-wrap">
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Total Keys: <strong>{entries.length}</strong>
          </span>
          {LANGUAGES.map(lang => {
            const langCode = lang.code as LanguageCode
            const count = missingCount[langCode]
            const isActive = showMissingFor === langCode
            return (
              <button
                key={lang.code}
                onClick={() => {
                  if (isActive) {
                    setShowMissingFor(null)
                  } else {
                    setShowMissingFor(langCode)
                  }
                }}
                className={`text-sm transition-colors ${
                  isActive 
                    ? 'font-semibold' 
                    : 'hover:opacity-80'
                }`}
                title={isActive ? 'Click to show all keys' : `Click to show ${count} missing ${lang.name} keys`}
              >
                {lang.flag} Missing:{' '}
                <strong className={count > 0 ? 'text-orange-600 ml-1 underline' : 'text-green-600 ml-1'}>
                  {count}
                </strong>
                {isActive && (
                  <span className="ml-2 text-xs text-blue-600">(filtered)</span>
                )}
              </button>
            )
          })}
          {showMissingFor && (
            <button
              onClick={() => setShowMissingFor(null)}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline"
            >
              Clear filter
            </button>
          )}
        </div>

        {/* AGC: Filters */}
        <div className="flex items-center gap-4">
          {/* AGC: Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search keys or values..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light focus:border-blue-500 bg-white dark:bg-admin-card-dark text-slate-900 dark:text-slate-100"
            />
          </div>

          {/* AGC: Namespace filter */}
          <select
            value={selectedNamespace}
            onChange={(e) => setSelectedNamespace(e.target.value as Namespace | 'all')}
            className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light focus:border-blue-500 bg-white dark:bg-admin-card-dark text-slate-900 dark:text-slate-100"
          >
            <option value="all">All Namespaces</option>
            {NAMESPACES.map(ns => (
              <option key={ns} value={ns}>{ns}</option>
            ))}
          </select>

          {/* AGC: Export buttons */}
          <div className="flex items-center gap-2">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => exportTranslations(lang.code as LanguageCode)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                title={`Export ${lang.name}`}
              >
                <Download className="w-4 h-4" />
                {lang.flag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* AGC: Messages */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {successMessage && (
        <div className="mx-6 mt-4 p-3 bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 rounded-lg flex items-center gap-2">
          <Check className="w-4 h-4" />
          {successMessage}
        </div>
      )}

      {/* AGC: Filter indicator */}
      {showMissingFor && (
        <div className="mx-6 mt-4 p-3 bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>
              Showing <strong>{filteredEntries.length}</strong> missing {LANGUAGES.find(l => l.code === showMissingFor)?.name} translation{filteredEntries.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setShowMissingFor(null)}
            className="text-info-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm"
          >
            Show all keys
          </button>
        </div>
      )}

      {/* AGC: Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-white/[0.02] border-b border-slate-200 dark:border-white/5">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                Namespace
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-48">
                Key
              </th>
              {LANGUAGES.map(lang => (
                <th key={lang.code} className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {lang.flag} {lang.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-white/5">
            {isLoading ? (
              <tr>
                <td colSpan={2 + LANGUAGES.length} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading translations...
                </td>
              </tr>
            ) : filteredEntries.length === 0 ? (
              <tr>
                <td colSpan={2 + LANGUAGES.length} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  {showMissingFor ? (
                    <div>
                      <p className="mb-2">No missing {LANGUAGES.find(l => l.code === showMissingFor)?.name} translations found!</p>
                      <button
                        onClick={() => setShowMissingFor(null)}
                        className="text-info-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline text-sm"
                      >
                        Show all keys
                      </button>
                    </div>
                  ) : (
                    'No translations found'
                  )}
                </td>
              </tr>
            ) : (
              filteredEntries.map((entry) => (
                <tr key={`${entry.namespace}.${entry.key}`} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400">
                      {entry.namespace}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-600 dark:text-slate-300">
                    {entry.key}
                  </td>
                  {LANGUAGES.map(lang => {
                    const langCode = lang.code as LanguageCode
                    const isEditing = editingCell?.key === `${entry.namespace}.${entry.key}` && editingCell?.lang === langCode
                    const value = entry.values[langCode]
                    const isMissing = entry.isMissing[langCode]

                    return (
                      <td key={lang.code} className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit()
                                if (e.key === 'Escape') cancelEdit()
                              }}
                            />
                            <button
                              onClick={saveEdit}
                              disabled={isSaving}
                              className="p-1 text-brand-primary dark:text-emerald-400 hover:bg-success-50 dark:hover:bg-emerald-500/15 rounded"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => startEdit(entry.key, entry.namespace, langCode, value)}
                            className={`cursor-pointer p-2 rounded hover:bg-slate-100 dark:hover:bg-white/5 min-h-[32px] ${
                              isMissing ? 'bg-orange-50 border border-orange-200' : ''
                            }`}
                          >
                            {isMissing ? (
                              <span className="text-orange-500 text-sm flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Missing
                              </span>
                            ) : (
                              <span className="text-slate-700 dark:text-slate-300">{value}</span>
                            )}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* AGC: Add Key Modal */}
      {showAddKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]">
          <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Add New Translation Key</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Namespace</label>
                <select
                  value={newKey.namespace}
                  onChange={(e) => setNewKey({ ...newKey, namespace: e.target.value as Namespace })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light bg-white dark:bg-admin-card-dark text-slate-900 dark:text-slate-100"
                >
                  {NAMESPACES.map(ns => (
                    <option key={ns} value={ns}>{ns}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Key</label>
                <input
                  type="text"
                  value={newKey.key}
                  onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                  placeholder="e.g., buttonLabel"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light bg-white dark:bg-admin-card-dark text-slate-900 dark:text-slate-100"
                />
              </div>

              {LANGUAGES.map(lang => (
                <div key={lang.code}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {lang.flag} {lang.name} {lang.code === 'en' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={newKey[lang.code as LanguageCode]}
                    onChange={(e) => setNewKey({ ...newKey, [lang.code]: e.target.value })}
                    placeholder={`Translation in ${lang.name}`}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light bg-white dark:bg-admin-card-dark text-slate-900 dark:text-slate-100"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddKeyModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addNewKey}
                disabled={isSaving || !newKey.key || !newKey.en}
                className="px-4 py-2 bg-brand-primary dark:bg-brand-light text-white rounded-lg hover:bg-brand-dark dark:hover:opacity-90 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Adding...' : 'Add Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Hidden file input for import */}
      <input
        type="file"
        id="import-file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            // AGC: For now, import to English. Could add lang selector
            importTranslations('en', file)
          }
          e.target.value = ''
        }}
      />
    </div>
  )
}

export default TranslationManager
